import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { IndianRupee, TrendingUp, Users, AlertTriangle, ArrowUpRight, RotateCcw, Wallet } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const rupee = (v) => "₹ " + Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const KPI = ({ label, value, hint, icon: Icon, testid }) => (
  <Card className="p-6 border-border bg-white gz-fade" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
        <p className="font-serif-elegant text-4xl mt-3 leading-none">{value}</p>
        {hint && <p className="text-xs text-muted-foreground mt-2">{hint}</p>}
      </div>
      <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center text-primary">
        <Icon size={18} />
      </div>
    </div>
  </Card>
);

const statusColor = {
  paid: "bg-green-100 text-green-800 border-green-200",
  partial: "bg-amber-100 text-amber-800 border-amber-200",
  unpaid: "bg-neutral-100 text-neutral-700 border-neutral-200",
  overdue: "bg-red-100 text-red-800 border-red-200",
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard/summary").then(r => setData(r.data)).catch(()=>{}); }, []);

  if (!data) return <div className="text-muted-foreground">Loading dashboard…</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Overview</p>
          <h1 className="font-serif-elegant text-5xl mt-1">Dashboard</h1>
        </div>
        <p className="text-sm text-muted-foreground">Snapshot of your studio's pulse</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
        <KPI testid="kpi-sales" label="Total Sales" value={rupee(data.total_sales)} hint={`This month: ${rupee(data.month_sales)}`} icon={IndianRupee} />
        <KPI testid="kpi-pending" label="Pending Payments" value={rupee(data.pending)} hint={`${data.overdue} overdue`} icon={AlertTriangle} />
        <KPI testid="kpi-customers" label="Customers" value={data.total_customers} hint={`${data.total_invoices} invoices`} icon={Users} />
        <KPI testid="kpi-paid" label="Collected" value={rupee(data.total_paid)} hint="Across all invoices" icon={TrendingUp} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <KPI testid="kpi-returns" label="Returns" value={data.total_returns || 0} hint={`${rupee(data.return_amount)} refunded · ${data.month_returns || 0} this month`} icon={RotateCcw} />
        <KPI testid="kpi-wallet" label="Wallet Credit Outstanding" value={rupee(data.wallet_outstanding)} hint="Advance balance customers can use on their next invoice" icon={Wallet} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:col-span-2 bg-white border-border">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Last 7 days</p>
              <h3 className="font-serif-elegant text-2xl">Sales Trend</h3>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.chart}>
                <XAxis dataKey="date" tickFormatter={(d)=>d.slice(5)} stroke="#5C5C5C" fontSize={11} />
                <YAxis stroke="#5C5C5C" fontSize={11} />
                <Tooltip formatter={(v)=>rupee(v)} contentStyle={{ background: "#fff", border: "1px solid #EBE8E0", borderRadius: 8 }} />
                <Line type="monotone" dataKey="total" stroke="#B35C44" strokeWidth={2.5} dot={{ r: 3, fill: "#B35C44" }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 bg-white border-border">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Top</p>
          <h3 className="font-serif-elegant text-2xl mb-4">Best Customers</h3>
          <div className="space-y-3">
            {(data.top_customers || []).length === 0 && <p className="text-sm text-muted-foreground">No sales yet.</p>}
            {data.top_customers.map((c, i) => (
              <div key={i} className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold">{c.name?.[0]}</div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{c.name}</p>
                    <p className="text-xs text-muted-foreground">{c.count} invoices</p>
                  </div>
                </div>
                <span className="text-sm font-semibold">{rupee(c.total)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-6 bg-white border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Recent</p>
            <h3 className="font-serif-elegant text-2xl">Latest Invoices</h3>
          </div>
          <Link to="/invoices" className="text-sm text-primary hover:underline flex items-center gap-1" data-testid="view-all-invoices">
            View all <ArrowUpRight size={14} />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="py-2">Number</th>
                <th className="py-2">Customer</th>
                <th className="py-2">Date</th>
                <th className="py-2 text-right">Amount</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_invoices?.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">No invoices yet. Create your first invoice →</td></tr>
              )}
              {data.recent_invoices?.map((inv) => (
                <tr key={inv.id} className="border-t hairline">
                  <td className="py-3"><Link to={`/invoices/${inv.id}`} className="font-medium hover:text-primary">{inv.number}</Link></td>
                  <td className="py-3">{inv.customer?.name}</td>
                  <td className="py-3 text-muted-foreground">{inv.date}</td>
                  <td className="py-3 text-right font-medium">{rupee(inv.total)}</td>
                  <td className="py-3">
                    <Badge variant="outline" className={`${statusColor[inv.status] || ""} capitalize`}>{inv.status}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
